import * as React from 'react';
import {
  AppBar as MineralAppBar,
  AppBarProps,
  Box,
  Divider,
  Grid,
  styled,
  Theme,
  ThemeMenu,
  Typography,
  IconButton,
  Tooltip,
} from '@mineral/core';
import { LogoType } from './LogoType';
import { UserMenu } from './UserMenu';
import { appContext } from 'api/ApplicationContext/AppContext';
import {
  colorCommonBlack,
  shadow1,
  paletteGrey200,
  paletteMineralBlue500,
  colorCommonWhite,
} from '@mineral/tokens';
import { RoutedBreadcrumb } from '@mineral-community/routed-breadcrumb';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ProgressIcon } from 'assets/icons/ProgressIcon';
import { QuestionMark as HelpIcon, Success } from '@mineral/icons';
import { ICEInitialize, ProgressState, ProgressStateUpdate } from 'App';
import { AppBarEnum, progressInformation, ProgressStatus } from './Enums';
import { Warning } from '@mui/icons-material';
import { useRoutedBreadcrumbLookup } from 'hooks/useRoutedBreadcrumbLookup';

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItem: 'center',
  height: theme.spacing(6),
  boxShadow: shadow1,
  border: `2px solid ${
    theme.palette.mode === 'light' ? colorCommonWhite : colorCommonBlack
  }`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(0.5),
}));

export const AppBar: React.FC<AppBarProps> = (props) => {
  const location = useLocation();
  const status = React.useContext(ProgressState);
  const setState = React.useContext(ProgressStateUpdate);
  const { localLookup, globalLookup } = useRoutedBreadcrumbLookup();
  const params = useParams();

  const updateGlobalLookup = (lookup: Map<string, string>) => {
    lookup.set('metadataGenerator', 'Metadata Generator');
    lookup.set('URL_Patterns', 'URL Patterns');
    lookup.set('Product_Version', 'Product Version');
    lookup.set('reindexContent', 'Reindex Content');
    lookup.set('settings', 'access');
    lookup.set('Localization_Table', 'Localization Table');
    lookup.set('Custom_Content', 'Custom Content');
    lookup.set('grant-access', 'Grant Access');
    lookup.set('revoke-access', 'Revoke Access');
    return lookup;
  };

  const initializeICE = React.useContext(ICEInitialize);
  const getIsBreadcrumbRequired = () => {
    const guids =
      params['*']
        ?.split('/')
        .filter((str) =>
          str.match(
            /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
          )
        ) || [];
    return guids.filter((key) => localLookup.get(key)).length === guids.length;
  };

  const [isBreadCrumbVisible, setIsBreadCrumbVisible] =
    React.useState<boolean>(false);

  const handleEnableBreadcrumb = () => {
    setIsBreadCrumbVisible(true);
  };

  window.addEventListener('noModuleItemFound', handleEnableBreadcrumb);

  useEffect(() => {
    setIsBreadCrumbVisible(false);
    return () => {
      setIsBreadCrumbVisible(false);
      window.removeEventListener('noModuleItemFound', handleEnableBreadcrumb);
    };
  }, []);
  useEffect(() => {
    setIsBreadCrumbVisible(false);
  }, [location]);
  useEffect(() => {
    if (status === ProgressStatus.saved || status === ProgressStatus.error) {
      const setNone = () => {
        setState(ProgressStatus.none);
      };
      setTimeout(setNone, 5000);
    }
  }, [setState, status]);
  return (
    <MineralAppBar
      sx={(theme: Theme) => {
        return {
          color:
            theme.palette.mode === 'light'
              ? paletteMineralBlue500
              : paletteGrey200,
          '& .MuiContainer-root': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
          borderBottom: '0px',
        };
      }}
      logo={
        <StyledBox>
          <LogoType title={appContext.AppTitle} />
          <Divider
            orientation="vertical"
            flexItem
            sx={(theme: Theme) => {
              return { height: theme.spacing(3.5), alignSelf: 'center' };
            }}
          />
          <Grid
            sx={(theme: Theme) => {
              return {
                paddingRight: theme.spacing(2),
                paddingLeft: theme.spacing(1),
                display: 'flex',
                wordBreak: 'break-all',
                overflow: 'auto',
                alignItems: 'center',
                '& span': {
                  color:
                    theme.palette.mode === 'light'
                      ? colorCommonBlack
                      : colorCommonWhite,
                },
              };
            }}>
            <Grid item sx={{ margin: 'auto' }}>
              {(getIsBreadcrumbRequired() || isBreadCrumbVisible) && (
                <RoutedBreadcrumb
                  globalLookup={updateGlobalLookup(globalLookup)}
                  localLookup={localLookup}
                />
              )}
            </Grid>
          </Grid>
        </StyledBox>
      }
      actions={
        <Box
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {status === ProgressStatus.saving && (
            <Grid sx={(theme) => ({ mr: theme.spacing(1), display: 'flex' })}>
              <Grid item>
                <ProgressIcon
                  color="primary"
                  sx={(theme) => ({
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                    mr: theme.spacing(1),
                  })}
                />
              </Grid>
              <Grid item>
                <Typography color="GrayText" sx={{ fontStyle: 'italic' }}>
                  {progressInformation.saving}
                </Typography>
              </Grid>
            </Grid>
          )}
          {status === ProgressStatus.saved && (
            <Grid sx={{ display: 'flex', mr: '12px' }}>
              <Grid item sx={(theme) => ({ mr: theme.spacing(1) })}>
                <Success color="primary" />
              </Grid>
              <Grid item>
                <Typography color="GrayText" sx={{ fontStyle: 'italic' }}>
                  {progressInformation.saved}{' '}
                </Typography>
              </Grid>
            </Grid>
          )}
          {status === ProgressStatus.error && (
            <Grid
              sx={(theme) => ({
                display: 'flex',
                mr: theme.spacing(1),
              })}>
              <Grid item sx={(theme) => ({ mr: theme.spacing(1) })}>
                <Warning color="error" />
              </Grid>
              <Grid item>
                <Typography color="red" variant="button">
                  {progressInformation.error}
                </Typography>
              </Grid>
            </Grid>
          )}
          <StyledBox
            sx={(theme: Theme) => {
              return {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
              };
            }}>
            <Box
              sx={(theme: Theme) => {
                return { marginRight: theme.spacing(1) };
              }}>
              <ThemeMenu />
            </Box>
            <Tooltip title={AppBarEnum.ICEHelp}>
              <IconButton
                onClick={initializeICE}
                sx={{ mr: 1 }}
                color="primary">
                <HelpIcon />
              </IconButton>
            </Tooltip>
            <UserMenu />
          </StyledBox>
        </Box>
      }
      color="transparent"
      {...props}
    />
  );
};
